import { IconArrowRight, IconBuildingBank, IconIdBadge2, IconUserCircle } from '@tabler/icons-react'

import type { Application as ApplicationType } from '@subaio/api-types/financedata'
import { ButtonGroup, FormattedMessage, Link } from '@subaio/components'
import { Form } from '@subaio/components/src/HookForm'
import { object } from '@subaio/validation'
import { Input } from '@subaio/webapp'

import { asContainer } from 'lib/container'
import { useValidation } from 'lib/useValidation'

import { Background } from 'containers/Login/Background'
import { BackgroundWrapper } from 'containers/Login/BackgroundWrapper'
import { Grid } from 'containers/Login/Grid'
import { SkewedContent } from 'containers/Login/SkewedContent'

import { ButtonSolidSubmit } from 'components/Buttons'
import { LoadingScreen } from 'components/LoadingScreen'
import { Logo } from 'components/Logo'

import { Application } from './Application'
import { Brand } from './Brand'
import { BrandLogo } from './BrandLogo'
import { BrandTitle } from './BrandTitle'
import { ContinueButton } from './ContinueButton'
import { Header } from './Header'
import { initializeMcpApplications } from './initializeMcpApplications'
import { Page } from './Page'
import { Submit } from './Submit'
import { submitOAuthForm } from './submitOAuthForm'

type ApplicationForm = {
	applicationId: string
}

type State = {
	applications: Array<ApplicationType>
}

export const container = asContainer<State>()({
	path: '/mcp-login',
	hideHeaderAndFooter: true,
	authentication: { type: 'public' },
	initialize: initializeMcpApplications,
	Running: ({ applications, query }) => {
		const { fieldEmail, fieldString } = useValidation()
		const applicationSelection = query?.application === '1'
		const correlation = typeof query?.correlation === 'string' ? query.correlation : ''
		const handoff = typeof query?.handoff === 'string' ? query.handoff : undefined
		const loginError = Boolean(query?.error)

		if (applicationSelection) {
			if (applications.length === 1) return <LoadingScreen />
			return (
				<>
					<Header>
						<Brand>
							<BrandLogo type='secondary' size={41} />
							<BrandTitle>Finance Data</BrandTitle>
						</Brand>
						<IconUserCircle aria-hidden='true' size={42} strokeWidth={1.2} />
					</Header>
					<Page>
						<h1>
							<FormattedMessage id='mcpLogin.applicationSelection.title' defaultMessage='Choose a data profile' />
						</h1>
						<p>
							<FormattedMessage
								id='mcpLogin.applicationSelection.description'
								defaultMessage='Select which data your Finance Data MCP should use. This determines the accounts, transactions and financial data available through this connection'
							/>
						</p>
						<Form<ApplicationForm>
							initialValues={{ applicationId: applications[0]?.id ?? '' }}
							schema={object({ applicationId: fieldString() })}
							onSubmit={({ applicationId }) =>
								submitOAuthForm('/mcp-oauth/oauth2/application', { applicationId, correlation })
							}
							render={() => (
								<>
									<Application
										isRequired
										label={
											<FormattedMessage id='mcpLogin.applicationSelection.fieldLabel' defaultMessage='Data profile' />
										}
										name='applicationId'
										options={applications.map((application) => {
											const isDemo = application.type === 'SANDBOX'

											return {
												value: application.id,
												icon: isDemo ? (
													<IconIdBadge2 aria-hidden='true' size={24} strokeWidth={1.5} />
												) : (
													<IconBuildingBank aria-hidden='true' size={24} strokeWidth={1.5} />
												),
												label: isDemo ? (
													<FormattedMessage id='mcpLogin.applicationSelection.demo.title' defaultMessage='Demo data' />
												) : (
													<FormattedMessage
														id='mcpLogin.applicationSelection.bank.title'
														defaultMessage='Your bank data'
													/>
												),
												hint: isDemo ? (
													<FormattedMessage
														id='mcpLogin.applicationSelection.demo.description'
														defaultMessage='Use realistic sample data from your selected test personas.'
													/>
												) : (
													<FormattedMessage
														id='mcpLogin.applicationSelection.bank.description'
														defaultMessage='Use data from the bank accounts you connected in Finance Data.'
													/>
												),
											}
										})}
									/>
									<Submit>
										<ButtonGroup center>
											<ContinueButton
												label={
													<>
														<FormattedMessage id='mcpLogin.applicationSelection.continue' defaultMessage='Continue' />
														<IconArrowRight aria-hidden='true' size={24} strokeWidth={1.2} />
													</>
												}
												submit
											/>
										</ButtonGroup>
									</Submit>
								</>
							)}
						/>
						<Link name='Home' target='_blank' rel='noreferrer noopener'>
							<FormattedMessage
								id='mcpLogin.applicationSelection.portalLink'
								defaultMessage='Manage bank connections and demo personas in the Financial Data portal'
							/>
						</Link>
					</Page>
				</>
			)
		}

		return (
			<Grid>
				<SkewedContent>
					<h1>Sign in to Finance Data MCP</h1>
					{loginError && <p role='alert'>The login details were invalid. Please try again.</p>}
					<Form
						schema={object({ email: fieldEmail(), password: fieldString() })}
						onSubmit={({ email, password }) =>
							submitOAuthForm('/mcp-oauth/login', { email, password, ...(handoff ? { handoff } : {}) })
						}
						render={() => (
							<>
								<Input name='email' label='Email' type='email' autoComplete='username' isRequired />
								<Input name='password' label='Password' type='password' autoComplete='password' isRequired />
								<ButtonGroup center>
									<ButtonSolidSubmit label='Sign in' />
								</ButtonGroup>
							</>
						)}
					/>
				</SkewedContent>
				<BackgroundWrapper>
					<Background>
						<Logo type='secondary' />
						<span>Finance Data</span>
					</Background>
				</BackgroundWrapper>
			</Grid>
		)
	},
})
